import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${API_BASE}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

// Auth now lives in an httpOnly cookie set by the backend, so there is no token
// for JS to attach. We keep credentials:true on every request so the cookie is
// sent automatically (same-site via the /api gateway).
api.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  googleSignIn: (credential) => api.post('/auth/google', { credential }),
  passwordPolicy: () => api.get('/auth/password-policy'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  deleteAccount: (data) => api.post('/auth/delete-account', data),
};

export const creditAPI = {
  myTransactions: () => api.get('/credit/transactions'),
};

export const notifyAPI = {
  unsubStatus: (token) => api.get('/unsubscribe/status', { params: { token } }),
  unsubscribe: (token) => api.post(`/unsubscribe?token=${encodeURIComponent(token)}`),
  resubscribe: (token) => api.post(`/resubscribe?token=${encodeURIComponent(token)}`),
};

// Products
export const productsAPI = {
  list: (params) => api.get('/products', { params }),
  get: (id) => api.get(`/products/${id}`),
  lookup: (ids) => api.post('/products/lookup', { ids }),
};

// Categories
export const categoriesAPI = {
  list: (params) => api.get('/categories', { params }),
  get: (id) => api.get(`/categories/${id}`),
};

// Main Categories
export const mainCategoriesAPI = {
  list: (params) => api.get('/main-categories', { params }),
};// Suppliers
export const suppliersAPI = {
  list: () => api.get('/suppliers'),
  get: (id) => api.get(`/suppliers/${id}`),
  listWithWindows: () => api.get('/suppliers/with-windows'),
  get: (id) => api.get(`/suppliers/${id}`),
  getFeatured: () => api.get('/settings/featured'),
};

// Order Windows
export const windowsAPI = {
  getActive: () => api.get('/order-windows/active'),
  list: () => api.get('/order-windows'),
};

// Orders
export const ordersAPI = {
  create: (data) => api.post('/orders', data),
  list: () => api.get('/orders'),
  unpaid: () => api.get('/orders/unpaid'),
  get: (id) => api.get(`/orders/${id}`),
  cancel: (id) => api.put(`/orders/${id}/cancel`),
  reorder: (id) => api.post(`/orders/${id}/reorder`),
  downloadInvoice: (id) => api.get(`/orders/${id}/invoice`, { responseType: 'blob' }),
};

// Collection Points
export const collectionPointsAPI = {
  list: () => api.get('/collection-points'),
  listAll: () => api.get('/collection-points/all'),
  create: (data) => api.post('/collection-points', data),
  update: (id, data) => api.put(`/collection-points/${id}`, data),
  delete: (id) => api.delete(`/collection-points/${id}`),
};

// Cart (server-side persistence)
export const cartAPI = {
  get: () => api.get('/cart'),
  save: (items) => api.put('/cart', { items }),
};

// Payments (Paystack hosted checkout + EFT)
export const paymentsAPI = {
  startPaystackCheckout: (orderId) => api.post(`/payments/paystack/initialize/${orderId}`),
  paystackStatus: (orderId) => api.get(`/payments/paystack/status/${orderId}`),
  confirm: (orderId) => api.post(`/payments/confirm/${orderId}`),
  getEftInstructions: (orderId) => api.get(`/payments/eft/instructions/${orderId}`),
};

// Public settings (banner, multi-supplier-cart toggle, featured config)
export const settingsAPI = {
  getPublic: () => api.get('/settings/featured'),
  getFeatured: () => api.get('/settings/featured'),
};

// Public About-page content (CMS)
export const aboutAPI = {
  get: () => api.get('/about'),
};

// Order Cycle Calendar (rolling 6-month vendor plan)
export const orderCycleAPI = {
  get: () => api.get('/order-cycle'),
  adminGet: () => api.get('/admin/order-cycle'),
  adminSet: (monthKey, supplierIds) => api.put(`/admin/order-cycle/${monthKey}`, { supplier_ids: supplierIds }),
};

// Admin
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  // About-page content
  getAbout: () => api.get('/about'),
  updateAbout: (data) => api.put('/admin/about', data),
  // Financial reports
  getReportMonths: () => api.get('/admin/reports/financial/months'),
  getFinancialReport: (params) => api.get('/admin/reports/financial', { params }),
  exportFinancialReport: (params) => api.get('/admin/reports/financial/export', { params, responseType: 'blob' }),
  getReportOrderDetail: (orderId) => api.get(`/admin/reports/order/${orderId}`),
  getCollectionCommission: (params) => api.get('/admin/reports/collection-commission', { params }),
  exportCollectionCommission: (params) => api.get('/admin/reports/collection-commission/export', { params, responseType: 'blob' }),
  // Orders
  listOrders: (params) => api.get('/admin/orders', { params }),
  exportOrders: (params) => api.get('/admin/orders/export', { params, responseType: 'blob' }),
  updateOrder: (id, data) => api.put(`/admin/orders/${id}`, data),
  reinstateOrder: (id) => api.post(`/admin/orders/${id}/reinstate`),
  updateOrderItems: (id, data) => api.put(`/admin/orders/${id}/items`, data),
  bulkUpdateOrderStatus: (orderIds, status) =>
    api.post('/admin/orders/bulk-status', { order_ids: orderIds, status }),
  resendWhatsApp: (orderId) => api.post(`/admin/orders/${orderId}/resend-whatsapp`),
  resendEmail: (orderId) => api.post(`/admin/orders/${orderId}/resend-email`),
  bulkResendWhatsApp: (orderIds, includeAlreadySent = false) =>
    api.post('/admin/orders/resend-whatsapp/bulk', { order_ids: orderIds, include_already_sent: includeAlreadySent }),
  getWhatsAppTemplates: (orderId) => api.get(`/admin/orders/${orderId}/whatsapp-templates`),
  sendWhatsAppTemplate: (orderId, template) => api.post(`/admin/orders/${orderId}/send-whatsapp`, { template }),
  listNotifications: (params) => api.get('/admin/notifications', { params }),
  notificationsSummary: (params) => api.get('/admin/notifications/summary', { params }),
  updateOrderContactPhone: (orderId, contactPhone) =>
    api.patch(`/admin/orders/${orderId}/contact-phone`, { contact_phone: contactPhone }),
  confirmStockRequestPayment: (payload) =>
    api.post('/admin/orders/confirm-stock-request-payment', payload),
  cancelMoqNotMet: (payload) =>
    api.post('/admin/orders/cancel-moq-not-met', payload),
  refundOrderItem: (orderId, productId, note = '', refundToCredit = false) =>
    api.post(`/admin/orders/${orderId}/refund-item`, { product_id: productId, note, refund_to_credit: refundToCredit }),
  refundOrderItems: (orderId, productIds, note = '', refundToCredit = false, quantities = null) =>
    api.post(`/admin/orders/${orderId}/refund-items`, { product_ids: productIds, note, refund_to_credit: refundToCredit, ...(quantities ? { quantities } : {}) }),
  adjustCredit: (userId, amount, reason = '') =>
    api.post(`/admin/users/${userId}/credit`, { amount, reason }),
  creditTransactions: (userId) => api.get(`/admin/users/${userId}/credit-transactions`),
  markOrderPaid: (orderId, note = '') =>
    api.post(`/admin/orders/${orderId}/mark-paid`, { note }),
  setPaymentStatus: (orderId, paymentStatus, note = '') =>
    api.put(`/admin/orders/${orderId}/payment-status`, { payment_status: paymentStatus, note }),
  deleteOrder: (id) => api.delete(`/admin/orders/${id}`),
  downloadInvoice: (id) => api.get(`/admin/orders/${id}/invoice`, { responseType: 'blob' }),
  // Packing list
  getPackingList: (params) => api.get('/admin/packing-list', { params }),
  setOrderPacked: (orderId, packed, supplierId) => api.put(`/admin/orders/${orderId}/packed`, supplierId ? { packed, supplier_id: supplierId } : { packed }),
  exportPackingList: (params) => api.get('/admin/packing-list/export', { params, responseType: 'blob' }),
  // Products
  listProducts: () => api.get('/admin/products'),
  exportProducts: (supplierId) => api.get('/admin/products/export', { params: supplierId && supplierId !== 'all' ? { supplier_id: supplierId } : {}, responseType: 'blob' }),
  createProduct: (data) => api.post('/admin/products', data),
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  productCartUsage: (id) => api.get(`/admin/products/${id}/cart-usage`),
  // Categories
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  // Main Categories
  listMainCategories: () => api.get('/admin/main-categories'),
  createMainCategory: (data) => api.post('/admin/main-categories', data),
  updateMainCategory: (id, data) => api.put(`/admin/main-categories/${id}`, data),
  deleteMainCategory: (id) => api.delete(`/admin/main-categories/${id}`),
  // Suppliers
  listSuppliers: () => api.get('/admin/suppliers'),
  createSupplier: (data) => api.post('/admin/suppliers', data),
  updateSupplier: (id, data) => api.put(`/admin/suppliers/${id}`, data),
  deleteSupplier: (id) => api.delete(`/admin/suppliers/${id}`),
  addSupplierNote: (id, text) => api.post(`/admin/suppliers/${id}/notes`, { text }),
  updateSupplierNote: (id, noteId, text) => api.put(`/admin/suppliers/${id}/notes/${noteId}`, { text }),
  deleteSupplierNote: (id, noteId) => api.delete(`/admin/suppliers/${id}/notes/${noteId}`),
  // Order Windows
  listWindows: () => api.get('/admin/order-windows'),
  createWindow: (data) => api.post('/admin/order-windows', data),
  updateWindow: (id, data) => api.put(`/admin/order-windows/${id}`, data),
  deleteWindow: (id) => api.delete(`/admin/order-windows/${id}`),
  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  // Email templates
  listEmailTemplates: () => api.get('/admin/email-templates'),
  updateEmailTemplate: (key, data) => api.put(`/admin/email-templates/${key}`, data),
  resetEmailTemplate: (key) => api.post(`/admin/email-templates/${key}/reset`),
  previewEmailTemplate: (key, data) => api.post(`/admin/email-templates/${key}/preview`, data),
  // Users
  listUsers: () => api.get('/admin/users'),
  exportUsers: () => api.get('/admin/users/export', { responseType: 'blob' }),
  toggleVip: (id) => api.put(`/admin/users/${id}/vip`),
  toggleCollectionFeeExempt: (id) => api.put(`/admin/users/${id}/collection-fee-exempt`),
  setUserRole: (id, role) => api.put(`/admin/users/${id}/role`, { role }),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  demoteRogueAdmins: () => api.post('/admin/users/demote-rogue-admins'),
  // Reports
  exportReport: (vendorId) => api.get(`/admin/reports/export/${vendorId}`, { responseType: 'blob' }),
  exportAggregatedReport: (supplierId) => api.get(`/admin/reports/aggregated/${supplierId}`, { responseType: 'blob' }),
  // Audit log
  listAuditLogs: (limit = 200) => api.get('/admin/audit-logs', { params: { limit } }),
  importSuppliers: (formData) => api.post('/admin/suppliers/import/bulk', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadSupplierTemplate: () => api.get('/admin/suppliers/template', { responseType: 'blob' }),
  exportSuppliers: () => api.get('/admin/suppliers/export', { responseType: 'blob' }),
  // Bulk Import
  downloadImportTemplate: () => api.get('/admin/products/import-template', { responseType: 'blob' }),
  bulkImportProducts: (file, mode = 'append') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', mode);
    return api.post('/admin/products/bulk-import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Upload
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Danger Zone
  resetCounts: () => api.get('/admin/reset/counts'),
  resetCollections: (collections, confirm) => api.post('/admin/reset', { collections, confirm }),
  // Purchase Orders
  listPOSuppliers: () => api.get('/admin/po/suppliers'),
  previewPO: (supplierId, mode, includeOrderIds) => api.get(`/admin/po/preview/${supplierId}`, {
    params: { ...(mode ? { mode } : {}), ...(includeOrderIds && includeOrderIds.length ? { include: includeOrderIds } : {}) },
    paramsSerializer: { indexes: null },
  }),
  sendPO: (supplierId, data) => api.post(`/admin/po/send/${supplierId}`, data),
  resendPO: (poId, data) => api.post(`/admin/po/${poId}/resend`, data),
  poHistory: (supplierId) => api.get('/admin/po/history', { params: supplierId ? { supplier_id: supplierId } : {} }),
  downloadPOPdf: (poId) => api.get(`/admin/po/${poId}/pdf`, { responseType: 'blob' }),
  downloadProvisionalPOPdf: (supplierId) => api.get(`/admin/po/provisional/${supplierId}/pdf`, { responseType: 'blob' }),
  downloadProvisionalPOExcel: (supplierId) => api.get(`/admin/po/provisional/${supplierId}/excel`, { responseType: 'blob' }),
  exportPOReconciliation: (poId) => api.get(`/admin/po/${poId}/reconciliation/export`, { responseType: 'blob' }),
  getPOReconciliationSummary: (params) => api.get('/admin/po/reconciliation', { params }),
  exportPOReconciliationSummary: (params) => api.get('/admin/po/reconciliation/export', { params, responseType: 'blob' }),
  getUncoveredOrders: (supplierId) => api.get(`/admin/po/uncovered/${supplierId}`),
  exportUncoveredOrders: (params) => api.get('/admin/po/reconciliation/uncovered/export', { params, responseType: 'blob' }),
  diagnoseOrderPO: (orderNumber) => api.get(`/admin/po/diagnose-order/${encodeURIComponent(orderNumber)}`),
  poOutOfWindowAlert: () => api.get('/admin/po/out-of-window-alert'),
  markPOOrdersOrdered: (supplierId, orderIds, note = '') =>
    api.post('/admin/po/mark-ordered', { supplier_id: supplierId, order_ids: orderIds, note }),
  unmarkPOOrdersOrdered: (supplierId, orderIds) =>
    api.post('/admin/po/unmark-ordered', { supplier_id: supplierId, order_ids: orderIds }),
};

// Seed
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Resolve a stored file/image path to a full URL. Uploaded images come back as
// relative API paths (e.g. "/api/uploads/<id>"); legacy/stock images may already
// be absolute http(s) URLs.
export const fileUrl = (u, w) => {
  if (!u) return '';
  if (/^https?:\/\//.test(u)) return u; // external URL — leave untouched
  const base = `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return w ? `${base}${u.includes('?') ? '&' : '?'}w=${w}` : base;
};

export default api;
